import React, { useEffect, useMemo, useState } from 'react';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { desktop, tablet } from '@shared/ui/atoms/MediaQueries';
import { useTranslation } from "react-i18next";
import { HorizontalSpacer } from '@shared/ui/atoms/VerticalSpacer';
import { Checkbox } from '@shared/ui/atoms/Checkbox';
import TextField from '@shared/ui/atoms/TextField';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import {
  addPendingArea,
  clearDeskEdits,
  renameArea,
  rotateDeskBy,
  setAreaCapacity,
  selectAreaEdits,
  selectDeskEdits,
  selectMapViewport,
  selectPendingDeskEditCount,
  selectSelectedAreaId,
  selectSelectedDeskIds,
  setSelectedDeskIds,
  setSidebarMode
} from "../ducks/manageDesks.duck";
import { SidebarMode } from "../data/ManageDesks.model";
import { fetchDesks, selectAllOfficeDesks } from "@features/desks/store/deskBooking.duck";
import { DialogIdentifiers, openDialog, openDialogWithPayload } from "@shared/store/dialog.duck";
import { AssignedTeamsAndTagsFilters } from "./floor-map-view/AssignedTeamsAndTagsFilters";
import { ActiveDeskControlForManageDesksSidebar } from "./floor-map-view/ActiveDeskControlForManageDesksSidebar";
import { IsBetaTester } from '@shared/hooks/IsBetaTester';
import { createDesk, Desk, updateDesk } from '@features/desks/services/DeskBookingService';
import {
  createOrUpdateOffice,
  MockEditableAreaEntity,
  OfficeEntity,
  updateSingleOfficeEntity
} from '@features/desks/services/AdvanceHotDeskingService';
import { officeToCreateOfficeRequest } from "@shared/utils/OfficeHelper";
import { fetchOfficeEntities, selectAllOfficeEntities } from "@features/desks/store/advanceHotDeskingSetup.duck";
import { selectCurrentUser } from "@shared/store/auth.duck";
import { failureNotification, successNotification } from "@shared/store/notification.duck";
import { selectOfficeFloorPlan, selectSelectedOffice } from "@features/whereabouts/store/officeView.duck";
import { Accordian } from '@shared/animations/AnimationVariants';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import Colours from '@shared/ui/atoms/Colours';

// Matches the coarse rotate step used by the standalone floor plan editor
const ROTATE_STEP_DEGREES = 22.5;
const DEFAULT_AREA_RECT = {areaX: 150, areaY: 150, areaWidth: 200, areaHeight: 150};

export function ManageDesksSidebar() {
  const dispatch = useDispatch();
  const desks = useSelector(selectAllOfficeDesks);
  const selectedDesks = useSelector(selectSelectedDeskIds);
  const {t} = useTranslation();

  const isBetaTester = IsBetaTester();
  const office = useSelector(selectSelectedOffice);
  const officeFloorPlan = useSelector(selectOfficeFloorPlan);
  const deskEdits = useSelector(selectDeskEdits);
  const areaEdits = useSelector(selectAreaEdits);
  const selectedAreaId = useSelector(selectSelectedAreaId);
  const mapViewport = useSelector(selectMapViewport);
  const allOffices = useSelector(selectAllOfficeEntities);
  const currentUser = useSelector(selectCurrentUser);
  const pendingDeskEditCount = useSelector(selectPendingDeskEditCount);
  const [busy, setBusy] = useState(false);
  const [deskAllocationOpen, setDeskAllocationOpen] = useState(false);
  const [deskEditorOpen, setDeskEditorOpen] = useState(false);

  // Desks can be dragged on the map while this section is collapsed — reveal it
  // so the pending count and save button aren't hidden.
  useEffect(() => {
    if (pendingDeskEditCount > 0) {
      setDeskEditorOpen(true);
    }
  }, [pendingDeskEditCount]);

  useEffect(() => {
    if (pendingDeskEditCount === 0) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [pendingDeskEditCount]);

  const selectAllDesks = (state: boolean) => {
    if (state) {
      dispatch(setSelectedDeskIds(desks.map(d => d.id)));
    } else {
      dispatch(setSelectedDeskIds([]));
    }
  }

  const selectedDesk = useMemo(() => {
    return selectedDesks.length === 1 ? desks.find((d: Desk) => d.id === selectedDesks[0]) : undefined;
  }, [desks, selectedDesks]);

  // Rotation the selected desk is currently rendered at, so rotate steps accumulate correctly
  const selectedDeskRotation = useMemo(() => {
    if (!selectedDesk) return 0;
    const edit = deskEdits[selectedDesk.id];
    if (edit?.rotation !== undefined) return edit.rotation;
    const deskFromPlan = (officeFloorPlan?.desks ?? []).find((p: any) => p.deskId === selectedDesk.id);
    return selectedDesk.rotation || deskFromPlan?.rotate || 0;
  }, [selectedDesk, deskEdits, officeFloorPlan]);

  const rotateSelectedDesk = (degrees: number) => {
    if (!selectedDesk) return;
    dispatch(rotateDeskBy({id: selectedDesk.id, degrees: degrees, baseRotation: selectedDeskRotation}));
  }

  const saveDeskEdits = async () => {
    setBusy(true);
    try {
      const desksOnPlan = (officeFloorPlan?.desks ?? []);
      const updates = Object.entries(deskEdits)
        .map(([deskId, edit]) => {
          const desk = desks.find((d: Desk) => d.id === parseInt(deskId));
          if (!desk) return undefined;
          const deskFromPlan = desksOnPlan.find((p: any) => p.deskId === desk.id);
          return updateDesk({
            ...desk,
            x: edit.x ?? (desk.x || deskFromPlan?.x || 0),
            y: edit.y ?? (desk.y || deskFromPlan?.y || 0),
            width: desk.width || deskFromPlan?.width || 48,
            height: desk.height || deskFromPlan?.height || 96,
            rotation: edit.rotation ?? (desk.rotation || deskFromPlan?.rotate || 0),
          });
        })
        .filter(Boolean);
      const areaUpdates = Object.entries(areaEdits)
        .map(([areaId, edit]) => {
          if (edit.isNew) {
            if (!office || !currentUser) return undefined;
            // Create the area, then write its rectangle onto the returned entity
            return createOrUpdateOffice(officeToCreateOfficeRequest({
              ...MockEditableAreaEntity,
              label: edit.label || t('floor-plan-editor.new-area'),
              capacity: edit.capacity ?? 0,
              parentId: office.id,
              companyId: currentUser.companyEntity.id,
            } as any as OfficeEntity)).then((created: OfficeEntity) => updateSingleOfficeEntity({
              ...created,
              areaX: edit.areaX,
              areaY: edit.areaY,
              areaWidth: edit.areaWidth,
              areaHeight: edit.areaHeight,
            }));
          }
          const area = allOffices.find((o: OfficeEntity) => o.id === parseInt(areaId));
          if (!area) return undefined;
          return updateSingleOfficeEntity({
            ...area,
            label: edit.label ?? area.label,
            capacity: edit.capacity ?? area.capacity,
            areaX: edit.areaX ?? area.areaX,
            areaY: edit.areaY ?? area.areaY,
            areaWidth: edit.areaWidth ?? area.areaWidth,
            areaHeight: edit.areaHeight ?? area.areaHeight,
          });
        })
        .filter(Boolean);

      await Promise.all([...updates, ...areaUpdates]);
      if (areaUpdates.length > 0) {
        dispatch(fetchOfficeEntities());
      }
      dispatch(clearDeskEdits());
      if (office) {
        dispatch(fetchDesks(office.id));
      }
      dispatch(successNotification(t('floor-plan-editor.save-success')));
    } catch {
      dispatch(failureNotification(t('floor-plan-editor.save-failed')));
    } finally {
      setBusy(false);
    }
  }

  const selectedAreaLabel = useMemo(() => {
    if (selectedAreaId === undefined) return undefined;
    const edit = areaEdits[selectedAreaId];
    if (edit?.label !== undefined) return edit.label;
    return allOffices.find((o: OfficeEntity) => o.id === selectedAreaId)?.label ?? '';
  }, [selectedAreaId, areaEdits, allOffices]);

  const selectedAreaCapacity = useMemo(() => {
    if (selectedAreaId === undefined) return 0;
    const edit = areaEdits[selectedAreaId];
    if (edit?.capacity !== undefined) return edit.capacity;
    return allOffices.find((o: OfficeEntity) => o.id === selectedAreaId)?.capacity ?? 0;
  }, [selectedAreaId, areaEdits, allOffices]);

  // Nothing is sent to the backend here — the area is held locally until save
  const addArea = () => {
    const width = DEFAULT_AREA_RECT.areaWidth;
    const height = DEFAULT_AREA_RECT.areaHeight;
    // Drop it in the middle of whatever the user is currently looking at
    const centre = mapViewport ? {
      x: (mapViewport.width / 2 - mapViewport.positionX) / mapViewport.scale,
      y: (mapViewport.height / 2 - mapViewport.positionY) / mapViewport.scale,
    } : {x: DEFAULT_AREA_RECT.areaX + width / 2, y: DEFAULT_AREA_RECT.areaY + height / 2};

    dispatch(addPendingArea({
      label: t('floor-plan-editor.new-area'),
      x: Math.round(centre.x - width / 2),
      y: Math.round(centre.y - height / 2),
      width: width,
      height: height,
    }));
  }

  const addDesk = async () => {
    if (desks.length === 0) {
      dispatch(failureNotification(t('floor-plan-editor.no-desks-warning')));
      return;
    }
    setBusy(true);
    try {
      await createDesk({
        ...desks[0],
        label: `Desk ${desks.length + 1}`,
        id: 0,
        active: true,
        x: 250,
        y: 250,
        width: 48,
        height: 96,
        rotation: 0,
      });
      if (office) {
        dispatch(fetchDesks(office.id));
      }
    } catch {
      dispatch(failureNotification(t('floor-plan-editor.save-failed')));
    } finally {
      setBusy(false);
    }
  }

  const close = () => {
    if (pendingDeskEditCount > 0 && !window.confirm(t('floor-plan-editor.unsaved-changes'))) {
      return;
    }
    dispatch(setSidebarMode(SidebarMode.ChooseOffice));
  }

  return (
    <Wrapper data-test="office-sidebar">
      {busy && <LoadingSpinner fullScreen={true} />}
      <HeadlineSmall style={{marginBottom: 12}}>{t('manage-desks-sidebar.edit-view')}</HeadlineSmall>
      <BodyRegular>{t('manage-desks-sidebar.total-desks', {total: desks.length})}</BodyRegular>
      <BodyRegular>{t('manage-desks-sidebar.selected-desks', {selected: selectedDesks.length})}</BodyRegular>
      <HorizontalSpacer />

      <SectionHeader onClick={() => setDeskAllocationOpen(!deskAllocationOpen)}
                     data-test="desk-allocation-header">
        <BodyRegular weight={600}>{t('manage-desks-sidebar.desk-allocation')}</BodyRegular>
        <Icon icon={deskAllocationOpen ? IconTypes.Down : IconTypes.Next} color={'grey'} />
      </SectionHeader>

      <Accordian isOpen={deskAllocationOpen}>
        <SectionContent>
          <Checkbox style={{marginBottom: 12}}
                    value={selectedDesks.length > 0 && selectedDesks.length === desks.length}
                    onChange={selectAllDesks}
                    disabled={desks.length === 0}
                    text={'manage-desks-sidebar.select-all-desks'} />

          <OutlineButton style={{marginBottom: 12}}
                         text={'manage-desks-sidebar.assign-to-indi'}
                         disabled={selectedDesks.length === 0}
                         click={() => dispatch(openDialog(DialogIdentifiers.AssignDesksToIndividual))}
                         fullWidth={true} />

          <OutlineButton style={{marginBottom: 12}}
                         text={'manage-desks-sidebar.assign-to-team'}
                         disabled={selectedDesks.length === 0}
                         click={() => dispatch(openDialog(DialogIdentifiers.AssignDesksToTeam))}
                         fullWidth={true} />

          <OutlineButton style={{marginBottom: 12}}
                         text={'manage-desks-sidebar.assign-to-workgroup'}
                         disabled={selectedDesks.length === 0}
                         click={() => dispatch(openDialog(DialogIdentifiers.AssignDesksToWorkgroup))}
                         fullWidth={true} />

          <ActiveDeskControlForManageDesksSidebar style={{marginBottom: 12}} />

          <OutlineButton style={{marginBottom: 12}}
                         text={'manage-desks-sidebar.reset-desks'}
                         disabled={selectedDesks.length === 0}
                         click={() => dispatch(openDialog(DialogIdentifiers.ResetDesks))}
                         fullWidth={true} />
        </SectionContent>
      </Accordian>

      {/* Spacer lives inside the section so hiding the editor doesn't leave two rules stacked together */}
      {isBetaTester && <>
        <HorizontalSpacer style={{marginTop: 12, marginBottom: 24}} />

        <SectionHeader onClick={() => setDeskEditorOpen(!deskEditorOpen)}
                       data-test="desk-editor-header">
          <BodyRegular weight={600}>{t('manage-desks-sidebar.desk-editor')}</BodyRegular>
          <Icon icon={deskEditorOpen ? IconTypes.Down : IconTypes.Next} color={'grey'} />
        </SectionHeader>

        <Accordian isOpen={deskEditorOpen}>
          <SectionContent>
            {selectedDesk && <>
              <BodyRegular style={{marginBottom: 12}} weight={600} data-test={'selected-desk-label'}>
                {t('floor-plan-editor.selected', {label: selectedDesk.label})}
              </BodyRegular>

              <ButtonPairRow>
                <OutlineButton text={t('floor-plan-editor.rotate-left', {degrees: ROTATE_STEP_DEGREES})}
                               disabled={busy}
                               click={() => rotateSelectedDesk(-ROTATE_STEP_DEGREES)}
                               dataTest={'rotate-desk-left-button'} />
                <OutlineButton text={t('floor-plan-editor.rotate-right', {degrees: ROTATE_STEP_DEGREES})}
                               disabled={busy}
                               click={() => rotateSelectedDesk(ROTATE_STEP_DEGREES)}
                               dataTest={'rotate-desk-right-button'} />
              </ButtonPairRow>

              <OutlineButton style={{marginBottom: 12}}
                             text={'manage-desks-sidebar.edit-desk'}
                             disabled={busy}
                             click={() => dispatch(openDialogWithPayload({
                               payload: {deskId: selectedDesk.id},
                               activeDialog: DialogIdentifiers.EditDeskDialog,
                             }))}
                             dataTest={'edit-desk-button'}
                             fullWidth={true} />
            </>}

            <OutlineButton style={{marginBottom: 12}}
                           text={'floor-plan-editor.add-desk'}
                           disabled={busy}
                           click={addDesk}
                           dataTest={'add-desk-button'}
                           fullWidth={true} />

            <OutlineButton style={{marginBottom: 12}}
                           text={'floor-plan-editor.add-area'}
                           disabled={busy || !office}
                           click={addArea}
                           dataTest={'add-area-button'}
                           fullWidth={true} />

            {selectedAreaId !== undefined && <>
              <TextField label={'manage-desks-sidebar.area-name'}
                         value={selectedAreaLabel ?? ''}
                         onChange={(val: string) => dispatch(renameArea({id: selectedAreaId, label: val}))}
                         dataTest={'area-name-input'}
                         fullWidth={true} />

              <TextField label={'settings.manage-desk.number-of-desks'}
                         type={'number'}
                         value={`${selectedAreaCapacity}`}
                         onChange={(val: string) => dispatch(setAreaCapacity({id: selectedAreaId, capacity: parseInt(val) || 0}))}
                         dataTest={'area-capacity-input'}
                         fullWidth={true} />
            </>}

            {pendingDeskEditCount > 0 && <>
              <BodyRegular style={{marginBottom: 12}} weight={600}>
                {t('manage-desks-sidebar.changed-desks-count', {count: pendingDeskEditCount})}
              </BodyRegular>
              <PrimaryButton style={{marginBottom: 12}}
                             text={'manage-desks-sidebar.save-desk-changes'}
                             disabled={busy}
                             click={saveDeskEdits}
                             dataTest={'save-desk-changes-button'}
                             fullWidth={true} />
            </>}
          </SectionContent>
        </Accordian>
      </>}

      <HorizontalSpacer style={{marginBottom: 32}} />

      <AssignedTeamsAndTagsFilters />

      <OutlineButton  style={{marginBottom: 12}} text={'button.close'} fullWidth={true} click={close} />
    </Wrapper>
  )
}

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 8px 0;
  transition: background-color .2s;
  &:hover {
    background-color: ${Colours.blue05};
  }
`

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 12px;
`

const ButtonPairRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
  button {
    min-width: unset !important;
  }
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  button {
    max-width: 100%;
  }
  @media (${tablet}) {
    margin-top: 0;
    max-width: 230px;
  }
  @media (${desktop}) {
    margin-top: 0;
  }
  .calendar {
    align-self: center;
  }
`
